/**
 * HostAudioEngine — miniaudio-backed audio for the native .NET engine player.
 *
 * A drop-in for the subset of @engine/audio's `AudioEngine` that the player's
 * `AudioSubsystem` uses, routing to the native `audioHost` bridge (miniaudio) instead
 * of the Web Audio API. `AudioSubsystem` selects it when running in the engine player
 * and a playback device is available; the browser keeps the real `AudioEngine`.
 *
 * @module @engine/player
 */

import type { HostAudioBridge } from '../env';

interface HostPlayOptions {
  volume?: number;
  pitch?: number;
  loop?: boolean;
  fadeIn?: number;
  busId?: string;
  position?: { x: number; y: number; z: number };
  spatial?: {
    enabled?: boolean;
    distanceModel?: 'linear' | 'inverse' | 'exponential';
    refDistance?: number;
    maxDistance?: number;
    rolloffFactor?: number;
    dopplerFactor?: number;
  };
}

/** Subset of AudioMixerBusDefinition needed to compute a bus's effective gain. */
interface MixerBusDef {
  id: string;
  volume?: number; // decibels
  muted?: boolean;
  parentBusId?: string | null;
}

/** ma_attenuation_model: 0 none, 1 inverse, 2 linear, 3 exponential. */
function distanceModelToInt(model?: string): number {
  switch (model) {
    case 'inverse': return 1;
    case 'linear': return 2;
    case 'exponential': return 3;
    default: return 1;
  }
}

/**
 * A playing voice. Mirrors the members of `AudioSourceInstance` that `AudioSubsystem`
 * actually touches (id, volume/pitch setters, playing, updatePositionTracked).
 */
class HostVoice {
  isSpatial = false;
  /** Effective linear gain of this voice's mixer bus; folded into every volume write. */
  busGain = 1;

  constructor(
    readonly id: number,
    private readonly host: HostAudioBridge,
  ) {}

  get playing(): boolean { return this.host.isVoicePlaying(this.id); }
  set volume(v: number) { this.host.setVoiceVolume(this.id, v * this.busGain); }
  set pitch(p: number) { this.host.setVoicePitch(this.id, p); }

  setPosition(x: number, y: number, z = 0): void {
    this.host.setVoicePosition(this.id, x, y, z);
  }

  updatePositionTracked(x: number, y: number, z: number, _dt: number): void {
    this.host.setVoicePosition(this.id, x, y, z);
  }
}

export class HostAudioEngine {
  private readonly _voices = new Map<number, HostVoice>();
  private readonly _busGain = new Map<string, number>();

  constructor(private readonly host: HostAudioBridge) {}

  get initialized(): boolean { return this.host.isAvailable; }

  async initialize(mixerDef?: unknown): Promise<void> {
    // Bake each bus's effective gain (own volume × parent chain, dB→linear) so a clip routed
    // to e.g. the 'music' bus (−6 dB by default) mixes correctly. The player never changes bus
    // volume at runtime, so per-voice baking suffices — no ma_sound_group needed.
    this._busGain.clear();
    const buses = (mixerDef as { buses?: MixerBusDef[] } | undefined)?.buses ?? [];
    const byId = new Map(buses.map((b) => [b.id, b]));
    const dbToLinear = (db: number): number => Math.pow(10, db / 20);
    const effective = (id: string, seen: Set<string>): number => {
      const b = byId.get(id);
      if (!b || seen.has(id)) return 1;
      seen.add(id);
      if (b.muted) return 0;
      const parent = b.parentBusId ? effective(b.parentBusId, seen) : 1;
      return dbToLinear(b.volume ?? 0) * parent;
    };
    for (const b of buses) this._busGain.set(b.id, effective(b.id, new Set()));
  }

  private busGain(busId?: string): number {
    return (busId !== undefined ? this._busGain.get(busId) : undefined) ?? 1;
  }

  isClipLoaded(guid: string): boolean { return this.host.isClipRegistered(guid); }

  async loadClip(guid: string, data: ArrayBuffer): Promise<void> {
    this.host.registerClip(guid, data);
  }

  play(clipGuid: string, options: HostPlayOptions = {}): HostVoice | null {
    const sp = options.spatial;
    const pos = options.position;
    const gain = this.busGain(options.busId);
    const id = this.host.play(clipGuid, {
      volume: (options.volume ?? 1) * gain,
      pitch: options.pitch ?? 1,
      loop: options.loop ?? false,
      fadeIn: options.fadeIn ?? 0,
      spatial: !!sp?.enabled,
      x: pos?.x ?? 0,
      y: pos?.y ?? 0,
      z: pos?.z ?? 0,
      attenuation: distanceModelToInt(sp?.distanceModel),
      minDistance: sp?.refDistance ?? 1,
      maxDistance: sp?.maxDistance ?? 10000,
      rolloff: sp?.rolloffFactor ?? 1,
      doppler: sp?.dopplerFactor ?? 1,
    });
    if (!id) return null;

    const voice = new HostVoice(id, this.host);
    voice.busGain = gain;
    voice.isSpatial = !!sp?.enabled;
    this._voices.set(id, voice);
    return voice;
  }

  stopVoice(voiceId: number): void {
    this.host.stop(voiceId);
    this._voices.delete(voiceId);
  }

  stopAll(): void {
    this.host.stopAll();
    this._voices.clear();
  }

  getActiveVoices(): HostVoice[] {
    const out: HostVoice[] = [];
    for (const [id, voice] of this._voices) {
      if (voice.playing) out.push(voice);
      else this._voices.delete(id); // reap finished one-shots
    }
    return out;
  }

  // miniaudio applies doppler/attenuation natively; mixer metering/buses are not modelled here.
  updateDoppler(_dt: number): void {}
  updateMetering(): void {}
  setDopplerConfig(_dopplerFactor: number, _speedOfSound: number): void {}

  setMasterVolume(volume: number): void {
    this.host.setMasterVolume(Math.max(0, volume));
  }

  updateListenerFrom2D(
    x: number,
    y: number,
    _rotation: number,
    _invertX?: boolean,
    _invertY?: boolean,
    _rotationOffset?: number,
    _followRotation?: boolean,
  ): void {
    // The engine places spatial sources at (x, 0, -y); match the listener so that a
    // source to the player's right (+x) pans right. Doppler uses per-source velocity.
    this.host.setListener(
      x, 0, -y,
      0, 0, -1,
      0, 1, 0,
      0, 0, 0,
    );
  }

  async shutdown(): Promise<void> {
    this.stopAll();
  }
}
